import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import DataTable from '../../components/DataTable';
import UserDetails from '../../components/popup/UserDetails';
import { ActionButtons, Switch } from '../../components/ActionButtons';

// Constants
import {
  ACTION_TYPE,
  DEBOUNCE_TIMEOUT,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  ROLES,
  TABLE_NAME
} from '../../constants/common';
import { HR_COLUMNS } from '../../constants/tableColumns';

// Prime-React Components
import { confirmDialog } from 'primereact/confirmdialog';

// Redux-Actions
import { userDetailsAction } from '../../redux/actions/userDetails';
import { getHrList, deleteHR } from '../../redux/actions/hr';
import { userStatus } from '../../redux/actions/interviewers';

// Utils
import { wordCapitalize, getUserRole, formatExperience } from '../../utils/common';
import { debounce } from '../../utils/debounce';

// Apis
import AddEditHR from './AddEditHR';
import { editUserDetailsAction } from '../../redux/actions/admin';
import CopyToClipboard from '../../components/CopyToClipboard';

const HRList = () => {
  const dispatch = useDispatch();
  const { hrs, userDetails } = useSelector((state) => state);
  const [hrId, setHrId] = useState(null);
  const [editHrId, setEditHrId] = useState(null);
  const [hrList, setHrList] = useState([]);
  const [hrExport, setHrExport] = useState([]);
  const [hrDetails, setHrDetails] = useState([]);
  const [tableStates, setTableStates] = useState({
    searchValue: '',
    pageNumber: INITIAL_PAGE_NUMBER,
    size: INITIAL_SIZE,
    sortBy: 'DateModified',
    orderBy: 'desc'
  });
  const [showPopup, setShowPopup] = useState(false);
  const onTogglePopup = () => setShowPopup((prevState) => !prevState);

  const [visibleRight, setVisibleRight] = useState(false);
  const onToggleSiderbar = () => setVisibleRight(!visibleRight);

  useEffect(() => {
    dispatch(getHrList(tableStates));
    if (hrId && hrId === editHrId) {
      dispatch(userDetailsAction(hrId));
    }
  }, [dispatch, tableStates, hrs?.isDeleted, hrs?.isUpdated, hrs?.isAdded]);

  useEffect(() => {
    const modifiedData = hrs?.data?.map((user) => {
      return {
        ...user,
        Mobile: (
          <div className="center">
            <span>{user.Mobile || '--'}</span>
            {user.Mobile && <CopyToClipboard text={user.Mobile} />}
          </div>
        ),
        Email: (
          <div className="center">
            <span>{user.Email || '--'}</span>
            {user.Email && <CopyToClipboard text={user.Email} />}
          </div>
        ),
        Experience: formatExperience(user?.YearsOfExperience, user?.MonthsOfExperience),
        Name: (
          <span className="table-view-popup-link" onClick={() => handleView(user.ID)}>
            {wordCapitalize(user.FirstName || '')} {wordCapitalize(user.LastName || '')}
          </span>
        ),
        'Soft Skills':
          user.TechStack?.length > 0
            ? user.TechStack?.length > 3
              ? user.TechStack?.slice(0, 3)
                  .map((tech) => wordCapitalize(tech))
                  .join(', ') + ' ...'
              : user.TechStack?.map((tech) => wordCapitalize(tech)).join(', ')
            : '--',
        action: (
          <div className="flex align-items-center">
            <Switch UserStatus={user.UserStatus} onChange={(e) => handleActive(e, user.ID)} />
            <ActionButtons onClick={(actionType, e) => handleRowActions(actionType, user.ID, e)} />
          </div>
        )
      };
    });
    setHrList(modifiedData);
  }, [hrs]);

  useEffect(() => {
    const modifiedList = hrs?.data?.map((user) => {
      const name = `${wordCapitalize(user.FirstName)} ${wordCapitalize(user.LastName)}`;
      return {
        Experience: formatExperience(user.YearsOfExperience, user?.MonthsOfExperience),
        Name: name,
        Mobile: (
          <div className="center">
            <span>{user.Mobile || '--'}</span>
            {user.Mobile && <CopyToClipboard text={user.Mobile} />}
          </div>
        ),
        Email: (
          <div className="center">
            <span>{user.Email || '--'}</span>
            {user.Email && <CopyToClipboard text={user.Email} />}
          </div>
        ),
        'Soft Skills':
          user.TechStack?.length > 0
            ? user.TechStack?.map((tech) => wordCapitalize(tech)).join(', ')
            : '--'
      };
    });
    setHrExport(modifiedList);
  }, [hrs]);

  useEffect(() => {
    hrId && dispatch(userDetailsAction(hrId));
  }, [dispatch, hrId]);

  const modifyUserDetails = useCallback(() => {
    if (userDetails?.ID) {
      setHrDetails([
        {
          title: 'Name',
          value: `${wordCapitalize(userDetails?.FirstName || '')} ${wordCapitalize(
            userDetails?.LastName || ''
          )}`
        },
        { title: 'Role', value: getUserRole(userDetails?.UserTypeId || '') },
        {
          title: 'Gender',
          value: userDetails?.Gender == 1 ? 'Male' : userDetails?.Gender == 2 ? 'Female' : '--'
        },
        ...(userDetails?.UserTypeId !== ROLES.HR_AND_INTERVIEWER
          ? [
              {
                title: 'Skills',
                value: userDetails?.TechStack?.map((tech) => wordCapitalize(tech)).join(' | ') || ''
              }
            ]
          : []),
        {
          title: 'Experience',
          value: formatExperience(userDetails?.YearsOfExperience, userDetails?.MonthsOfExperience)
        },
        {
          title: 'Email Address',
          value: (
            <div className="center">
              <span>{userDetails?.Email || '--'}</span>
              {userDetails?.Email && <CopyToClipboard text={userDetails?.Email} />}
            </div>
          )
        },
        {
          title: 'Mobile',
          value: (
            <div className="center">
              <span>{userDetails?.Mobile || '--'}</span>
              {userDetails?.Mobile && <CopyToClipboard text={userDetails?.Mobile} />}
            </div>
          )
        },
        { title: 'Address', value: userDetails?.Address || '' }
      ]);
    }
  }, [userDetails]);

  useEffect(() => {
    modifyUserDetails();
  }, [userDetails, modifyUserDetails]);

  const handlePagination = (pageNumber, size) =>
    setTableStates((prevState) => {
      return {
        ...prevState,
        pageNumber,
        size
      };
    });

  const handleSearch = (searchValue) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        searchValue
      };
    });
  };

  const handleSort = (sortBy) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  const handleView = (ID) => {
    if (ID) {
      onTogglePopup();
      setHrId(ID);
    }
  };

  const handleRowActions = (actionType, rowId) => {
    if (actionType === ACTION_TYPE.VIEW) {
      onTogglePopup();
      setHrId(rowId);
    } else if (actionType === ACTION_TYPE.EDIT) {
      onToggleSiderbar();
      setEditHrId(rowId);
      dispatch(editUserDetailsAction(rowId));
      document.body.classList.add('sidebar-open');
    } else if (actionType === ACTION_TYPE.DELETE) {
      confirmDialog({
        header: 'Delete',
        message: 'Are you sure you want to delete?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => dispatch(deleteHR(rowId))
      });
    }
  };

  const handleActive = (event, id) => {
    dispatch(userStatus({ id, UserStatus: event.value }));
  };

  const handleAdd = () => {
    setEditHrId(null);
    onToggleSiderbar();
    setVisibleRight(true);
    document.body.classList.add('sidebar-open');
  };

  return (
    <>
      <UserDetails header="HR Details" data={hrDetails} onHide={onTogglePopup} show={showPopup} />
      <AddEditHR ID={editHrId} onHide={onToggleSiderbar} show={visibleRight} />
      <DataTable
        name={TABLE_NAME.HR}
        searchPlaceHolder="Search by HR name, Email and Mobile"
        columns={HR_COLUMNS}
        data={hrList}
        exportData={hrExport}
        totalRecords={hrs.pagination.totalRecords}
        onPagination={handlePagination}
        pageNumber={tableStates.pageNumber}
        pageSize={tableStates.size}
        onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
        onAdd={() => handleAdd()}
        onSort={handleSort}
      />
    </>
  );
};

export default HRList;
